
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface CommunityHelperCategory {
  id: string;
  name: string;
  color: string;
  display_order: number;
  is_active: boolean;
}

interface CommunityHelper {
  id: string;
  name: string;
  description: string;
  image_url: string;
  tools: string[];
  uniform: string;
  workplace: string;
  fun_fact: string;
  category_id: string | null;
  display_order: number;
  is_active: boolean;
  community_helper_categories?: { name: string };
}

export const useCommunityHelpersData = () => {
  const [categories, setCategories] = useState<CommunityHelperCategory[]>([]);
  const [helpers, setHelpers] = useState<CommunityHelper[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  // Memoize filtered helpers for better performance
  const filteredHelpers = useMemo(() => {
    if (selectedCategoryFilter === "all") {
      return helpers;
    }
    return helpers.filter(helper => helper.category_id === selectedCategoryFilter);
  }, [helpers, selectedCategoryFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesResponse, helpersResponse] = await Promise.all([
        supabase
          .from("community_helper_categories")
          .select("*")
          .order("display_order"),
        supabase
          .from("community_helpers")
          .select(`
            *,
            community_helper_categories (name)
          `)
          .order("display_order")
      ]);

      if (categoriesResponse.error) throw categoriesResponse.error;
      if (helpersResponse.error) throw helpersResponse.error;

      setCategories(categoriesResponse.data || []);
      setHelpers(helpersResponse.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm("Are you sure you want to delete this category?")) return;

    try {
      const { error } = await supabase
        .from("community_helper_categories")
        .delete()
        .eq("id", categoryId);

      if (error) throw error;
      toast({ title: "Category deleted successfully" });
      fetchData();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive",
      });
    }
  };

  const handleDeleteHelper = async (helperId: string) => {
    if (!confirm("Are you sure you want to delete this community helper?")) return;

    try {
      const { error } = await supabase
        .from("community_helpers")
        .delete()
        .eq("id", helperId);

      if (error) throw error;
      toast({ title: "Community Helper deleted successfully" });
      fetchData();
    } catch (error) {
      console.error("Error deleting community helper:", error);
      toast({
        title: "Error",
        description: "Failed to delete community helper",
        variant: "destructive",
      });
    }
  };

  return {
    categories,
    helpers,
    filteredHelpers,
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    loading,
    fetchData,
    handleDeleteCategory,
    handleDeleteHelper,
  };
};
